import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
// utils
import axios from '../utils/axios';
import { isValidToken, setSession } from '../utils/jwt';

import Request from '../services/request';
// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    console.log('Auth initialized:', { isAuthenticated, userEmail: user?.email }, user);
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  }
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  logInWithSocial: () => Promise.resolve()
});

AuthProvider.propTypes = {
  children: PropTypes.node
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);

          const response = await Request.send({
            method: 'get',
            path: 'admin/me',
            headers: {
              authorization: `Bearer ${accessToken}`
            }
          });
          const { data, errors } = response;
        
          if (!errors || errors?.length === 0) {
            
            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: true,
                user: data.user
              }
            });
          } else {
            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: false,
                user: null
              }
            });
          }
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null
            }
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    };

    initialize();
  }, []);

  const login = async (email, password) => {
    // Import User service for admin login
    const User = (await import('../services/user')).default;
    
    // Try admin login first
    const response = await User.adminLogin(email, password);
    
    // Handle the nested response structure from your API
    if (response.success && response.data ) {
      const { accessToken, user } = response.data;
      
      
      if (accessToken && user) {
        setSession(accessToken);
        window.localStorage.setItem('isAuthenticated', true);
        window.localStorage.setItem('user', JSON.stringify(user));
        dispatch({
          type: 'LOGIN',
          payload: {
            user: user
          }
        });
        console.log('Login successful for user:', user.email);
        return { success: true };
      }
    }
    
    // If admin login fails, try regular user login as fallback
    const fallbackResponse = await Request.send({
      method: 'post',
      path: `admin/login`,
      data: {
        email,
        password
      }
    });
    
    const { data, errors } = fallbackResponse;
    if (!errors) {
      const { accessToken, user } = data;
      setSession(accessToken);
      delete data?.token;
      window.localStorage.setItem('isAuthenticated', true);
      window.localStorage.setItem('user', JSON.stringify(user));
      dispatch({
        type: 'LOGIN',
        payload: {
          user: user
        }
      });
    } else {
      return { errors };
    }
  };

  const logInWithSocial = async (dataInput) => {
    const response = await Request.send({
      method: 'post',
      path: `user/loinSocial`,
      data: dataInput
    });
    const { data, errors } = response;
    if (!errors) {
      const { token } = data;
      setSession(token);
      delete data?.token;
      window.localStorage.setItem('isAuthenticated', true);
      window.localStorage.setItem('user', JSON.stringify(data));
      dispatch({
        type: 'LOGIN',
        payload: {
          user: data
        }
      });
    } else {
      return { errors };
    }
  };

  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post('/api/account/register', {
      email,
      password,
      firstName,
      lastName
    });
    const { accessToken, user } = response.data;

    window.localStorage.setItem('accessToken', accessToken);
    dispatch({
      type: 'REGISTER',
      payload: {
        user
      }
    });
  };

  const logout = async () => {
    setSession(null);
    dispatch({ type: 'LOGOUT' });
  };

  const resetPassword = () => {};

  const updateProfile = () => {};

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
        resetPassword,
        updateProfile,
        logInWithSocial
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
